import { motion } from "framer-motion";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useEffect, useRef, useState } from "react";
import {
    FaBalanceScale,
    FaBuilding,
    FaCheckCircle,
    FaCookie,
    FaCopyright,
    FaEnvelope,
    FaFileContract,
    FaGavel,
    FaGlobe,
    FaLock,
    FaShieldAlt
} from "react-icons/fa";
import {
    TiArrowForward,
    TiArrowRight,
    TiArrowUp
} from "react-icons/ti";
import { Link } from "react-router-dom";
import AnimatedTitle from "./AnimatedTitle";
import Footer from "./Footer";
import ScrollDownButton from './ScrollDownButton';
import GlassButton from './ui/GlassButton';
import { SparklesCore } from './ui/SparklesCore';

gsap.registerPlugin(ScrollTrigger);

const Legal = () => {
  const [scrollDownClicked, setScrollDownClicked] = useState(false);
  const [isBottom, setIsBottom] = useState(false);

  const sectionRefs = useRef([]);
  const scrollDownRef = useRef(null);
  const scrollTopRef = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    sectionRefs.current.forEach((section, index) => {
      if (section) {
        gsap.fromTo(
          section,
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            delay: index * 0.1,
            scrollTrigger: {
              trigger: section,
              start: "top 90%",
            },
          }
        );
      }
    });

    if (scrollDownRef.current) {
      gsap.fromTo(
        scrollDownRef.current,
        { opacity: 0, y: -20 },
        { opacity: 1, y: 0, duration: 1 }
      );
    }
  }, []);

  const checkIfBottom = () => {
    if (window.innerHeight + window.scrollY >= document.body.offsetHeight - 200) {
      setIsBottom(true);
    } else {
      setIsBottom(false);
    }
  };

  const scrollToContent = () => {
    const content = document.getElementById("content");
    if (content) {
      const contentOffsetTop = content.offsetTop;
      window.scrollTo({
        top: contentOffsetTop - 100,
        behavior: "smooth",
      });
    }
    setScrollDownClicked(true);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setScrollDownClicked(false);
  };

  useEffect(() => {
    window.addEventListener("scroll", checkIfBottom);
    return () => {
      window.removeEventListener("scroll", checkIfBottom);
    };
  }, []);

  const legalSections = [
    {
      id: "LG-01",
      title: "Terms of Service",
      subtitle: "Sovereign Agreement Protocol",
      desc: "By accessing and using the Zero-Ecosystem, you agree to abide by the institutional terms and conditions. Review your rights and responsibilities.",
      icon: <FaFileContract />,
      link: "/terms-of-use",
      color: "emerald"
    },
    {
      id: "LG-02",
      title: "Privacy Policy",
      subtitle: "Data Sovereignty Protocol",
      desc: "Your privacy is our priority. Understand how we collect, use, and protect your personal data with institutional-grade encryption.",
      icon: <FaLock />,
      link: "/privacy-policy",
      color: "purple"
    },
    {
      id: "LG-03",
      title: "Sales Policy",
      subtitle: "Transaction Protocol",
      desc: "Review our commerce framework including pricing, payments, refunds, and digital delivery protocols across the ecosystem.",
      icon: <FaBalanceScale />,
      link: "/sales-policy",
      color: "amber"
    },
    {
      id: "LG-04",
      title: "Compliance",
      subtitle: "Integrity Protocol",
      desc: "We comply with all applicable laws including GDPR and CCPA. Access our whitepaper vault and regulatory deployment infrastructure.",
      icon: <FaShieldAlt />,
      link: "/compliance",
      color: "indigo"
    },
    {
      id: "LG-05",
      title: "Intellectual Property",
      subtitle: "Asset Protection Protocol",
      desc: "All content, trademarks, logos, and text on this platform is protected by international intellectual property laws.",
      icon: <FaCopyright />,
      link: "#",
      color: "cyan"
    },
    {
      id: "LG-06",
      title: "Cookie Policy",
      subtitle: "Browser Enhancement Protocol",
      desc: "We use cookies to enhance your browsing experience. By continuing to use our site, you consent to our Cookie Policy.",
      icon: <FaCookie />,
      link: "#",
      color: "orange"
    }
  ];

  return (
    <div className="w-screen min-h-screen bg-black text-blue-50 font-sans">
      {/* Hero Section */}
      <div className="relative w-full h-screen flex flex-col items-center justify-center p-5">
        <div className="relative w-full max-w-6xl h-64 md:h-[500px] overflow-hidden rounded-[4rem] border border-white/10 z-10 bg-black shadow-[0_0_80px_rgba(34,211,238,0.15)]">
          <video
            src="/videos/legal-hero.mp4"
            autoPlay
            loop
            muted
            playsInline
            className="absolute left-0 top-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black" />
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <SparklesCore
              id="legalHeroSparkles"
              background="transparent"
              minSize={0.6}
              maxSize={1.4}
              particleDensity={50}
              particleColor="#22d3ee"
              className="absolute inset-0"
            />
            <AnimatedTitle
              title="Sovereign Legal"
              containerClass="text-center text-blue-50 font-zentry relative z-10"
            />
            <motion.p 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="text-cyan-400/80 font-bold tracking-[0.4em] uppercase text-[10px] mt-6 z-10"
            >
              Institutional Governance & Legal Framework
            </motion.p>
          </div>
        </div>
      </div>

      {/* Scroll Down Button */}
      {!scrollDownClicked && (
        <ScrollDownButton
          ref={scrollDownRef}
          onClick={scrollToContent}
        />
      )}

      {/* Content Section */}
      <div id="content" className="px-6 md:px-10 py-32 mx-auto max-w-7xl">
        
        {/* Legal Framework Header */}
        <div className="mb-24 p-12 rounded-[4rem] bg-gradient-to-r from-slate-500/10 to-transparent border border-white/10">
           <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8">
              <div>
                 <div className="text-[10px] font-bold text-slate-500 uppercase tracking-[0.4em] mb-4">Governance Framework</div>
                 <h2 className="text-3xl md:text-4xl font-bold text-white tracking-widest leading-tight">Legal Information Hub</h2>
              </div>
              <div className="flex flex-col gap-2 text-right">
                 <div className="text-[9px] font-bold text-gray-600 uppercase tracking-widest">Jurisdiction</div>
                 <div className="text-sm font-bold text-white">United Kingdom</div>
                 <div className="text-[9px] font-bold text-gray-600 uppercase tracking-widest mt-2">Governing Law</div>
                 <div className="text-sm font-bold text-slate-400">UK Financial Law</div>
              </div>
           </div>
        </div>

        {/* Legal Navigation Bento */}
        <div className="mb-48">
           <div className="mb-16 flex flex-col md:flex-row justify-between items-end gap-8">
              <div className="max-w-xl">
                 <div className="text-[10px] font-bold text-zinc-500 uppercase tracking-[0.4em] mb-4">Legal Directives</div>
                 <h2 className="text-4xl md:text-5xl font-bold text-white tracking-widest leading-tight">Policy Archive</h2>
              </div>
              <p className="text-gray-300 italic text-sm max-w-xs md:text-right">
                 Navigate the complete legal framework governing the Zero-Ecosystem.
              </p>
           </div>

           <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {legalSections.map((section, index) => (
                <Link to={section.link} key={section.id}>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className={`relative group rounded-[3rem] bg-white/5 border border-white/10 hover:border-${section.color}-500/30 transition-all hover:bg-white/10 overflow-hidden flex flex-col p-10 h-full cursor-pointer`}
                    ref={(el) => (sectionRefs.current[index] = el)}
                  >
                    <div className={`absolute top-8 right-10 text-4xl text-${section.color}-500/20 group-hover:scale-110 transition-transform`}>
                       {section.icon}
                    </div>
                    <div className="text-[9px] font-bold text-gray-500 uppercase tracking-widest mb-2">{section.id}</div>
                    <h3 className="text-xl font-bold text-white mb-2 tracking-widest">{section.title}</h3>
                    <div className={`text-[10px] font-bold text-${section.color}-400 uppercase tracking-[0.2em] mb-6`}>{section.subtitle}</div>
                    <p className="text-gray-300 text-xs leading-relaxed mb-8 flex-grow">{section.desc}</p>
                    
                    <motion.div 
                      whileHover={{ x: 5 }}
                      className={`mt-auto text-${section.color}-500 flex items-center gap-2`}
                    >
                       <span className="text-[10px] font-bold uppercase tracking-widest">View Policy</span>
                       <TiArrowForward className="text-xl" />
                    </motion.div>
                  </motion.div>
                </Link>
              ))}
           </div>
        </div>

        {/* Legal Status Terminal */}
        <div className="mb-48">
           <div className="p-2 rounded-[4rem] bg-gradient-to-br from-slate-500/20 to-transparent border border-white/10 overflow-hidden shadow-2xl">
              <div className="bg-black/80 backdrop-blur-3xl p-16 relative">
                 <div className="flex flex-col lg:flex-row gap-16 items-center">
                    <div className="flex-1">
                       <div className="flex items-center gap-4 mb-8">
                          <div className="w-16 h-16 rounded-2xl bg-slate-500/20 flex items-center justify-center text-slate-400 text-3xl">
                             <FaGavel />
                          </div>
                          <div>
                             <div className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.4em]">Legal Engine</div>
                             <h3 className="text-2xl font-bold text-white tracking-widest">Governance Status</h3>
                          </div>
                       </div>
                       <div className="grid grid-cols-2 gap-6">
                          {[
                            { l: "Jurisdiction", v: "United Kingdom" },
                            { l: "GDPR Status", v: "Fully Compliant" },
                            { l: "CCPA Status", v: "Fully Compliant" },
                            { l: "Legal Contact", v: "legal@zero-blockchain.com" }
                          ].map((item, i) => (
                            <div key={i} className="flex flex-col gap-1">
                               <div className="text-[8px] font-bold text-gray-600 uppercase tracking-widest">{item.l}</div>
                               <div className="text-sm font-bold text-white italic tracking-wider">{item.v}</div>
                            </div>
                          ))}
                       </div>
                    </div>

                    <div className="flex-shrink-0 flex flex-col items-center gap-6">
                       <div className="w-32 h-32 rounded-[2rem] bg-emerald-600 border-2 border-emerald-400 flex items-center justify-center text-5xl text-white shadow-[0_0_40px_rgba(16,185,129,0.4)]">
                          <FaCheckCircle />
                       </div>
                       <div className="text-center">
                          <div className="text-sm font-bold text-emerald-400 uppercase tracking-widest">Fully Compliant</div>
                          <div className="text-[9px] text-gray-600 mt-2">All Protocols Active</div>
                       </div>
                    </div>
                 </div>

                 <div className="mt-16 pt-8 border-t border-white/5">
                    <div className="w-full h-2 rounded-full bg-white/5 overflow-hidden">
                       <motion.div 
                        initial={{ width: 0 }}
                        whileInView={{ width: "100%" }}
                        transition={{ duration: 2.5, ease: "easeOut" }}
                        className="h-full bg-gradient-to-r from-slate-600 to-zinc-600 shadow-[0_0_15px_rgba(100,116,139,0.5)]"
                       />
                    </div>
                    <div className="mt-4 flex justify-between text-[8px] font-bold text-gray-600 uppercase tracking-widest">
                       <span>Legal Framework Active</span>
                       <span>100%</span>
                    </div>
                 </div>
              </div>
           </div>
        </div>

        {/* Final CTA */}
        <motion.div
           initial={{ opacity: 0, scale: 0.95 }}
           whileInView={{ opacity: 1, scale: 1 }}
           viewport={{ once: true }}
           className="relative text-center py-48"
        >
           <div className="absolute inset-0 bg-slate-500/5 rounded-[100%] blur-[150px]" />
           <div className="relative z-10 max-w-4xl mx-auto px-6">
              <div className="w-24 h-24 rounded-[3rem] bg-slate-500/10 border border-slate-500/20 flex items-center justify-center text-5xl text-slate-400 mx-auto mb-16 shadow-[0_0_50px_rgba(100,116,139,0.2)]">
                <FaEnvelope />
              </div>
              <h2 className="text-5xl md:text-7xl font-bold mb-12 leading-tight">
                 Contact <span className="font-zentry italic text-slate-400 text-8xl uppercase tracking-tighter block mt-4">Legal</span> Hub
              </h2>
              <p className="text-gray-300 text-xl leading-relaxed mb-20 max-w-2xl mx-auto italic">
                For any legal inquiries, compliance questions, or institutional partnerships.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-8 justify-center items-center">
                 <GlassButton
                   variant="gray"
                   size="lg"
                   className="px-16 py-8 text-xl"
                   onClick={() => {}}
                 >
                   legal@zero-blockchain.com
                 </GlassButton>
                 
                 <GlassButton
                   variant="blue"
                   size="lg"
                   className="px-16 py-8 text-xl"
                   onClick={() => {}}
                 >
                   <div className="flex items-center gap-4 group">
                     Download Legal Pack <TiArrowRight className="text-2xl text-slate-400 group-hover:translate-x-2 transition-transform" />
                   </div>
                 </GlassButton>
              </div>

              <div className="mt-32 flex flex-wrap justify-center gap-16 opacity-30 grayscale hover:grayscale-0 transition-all duration-700">
                 <div className="flex items-center gap-2 font-bold tracking-[0.3em] text-[9px]"><FaGlobe /> UK JURISDICTION</div>
                 <div className="flex items-center gap-2 font-bold tracking-[0.3em] text-[9px]"><FaShieldAlt /> GDPR COMPLIANT</div>
                 <div className="flex items-center gap-2 font-bold tracking-[0.3em] text-[9px]"><FaBuilding /> INSTITUTIONAL GRADE</div>
              </div>
           </div>
        </motion.div>

      </div>

      {/* Back to Top */}
      {isBottom && (
        <div
          ref={scrollTopRef}
          className="fixed bottom-10 left-5 flex items-center gap-3 px-5 py-3 bg-black/40 backdrop-blur-sm border border-slate-500/30 rounded-full shadow-lg shadow-slate-500/10 cursor-pointer hover:bg-black/60 hover:border-slate-400/50 transition-all duration-500 z-50 group"
          onClick={scrollToTop}
        >
          <TiArrowUp className="text-slate-400 group-hover:-translate-y-1 transition-transform duration-500" size={24} />
          <span className="relative z-20 text-sm uppercase text-white/80 font-medium tracking-wider">BACK TO TOP</span>
        </div>
      )}

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Legal;
